import { QRCodeSVG } from "qrcode.react";
import LogoArSvg from "@/components/LogoArSvg";

export type InvoiceItem = {
  id: string | number;
  name: string;
  price: string;
  quantity: number;
  // Optional backend-provided per-item fields
  tax?: number;
  priceBeforeTax?: number;
  priceAfterTax?: number;
};

export type Invoice = {
  id: number;
  clientName: string;
  phone: string;
  discountCode: string;
  totalPrice: number;
  cashAmount: number;
  cardAmount: number;
  date: string;
  time: string;
  items: InvoiceItem[];
  // Optional QR code payload coming from backend (string/number)
  qrCode?: string | number;
  // Backend-provided totals (preferred over any local calculations)
  totalBeforeDiscount?: number;
  totalBeforeTax?: number;
  totalTax?: number;
  totalPriceFinal?: number;
  discountPercentage?: number;
};

const InvoicePrintContent = ({ invoice }: { invoice: Invoice }) => {
  const items = invoice.items ?? [];
  const fmt = (n?: number) => Number(n ?? 0).toFixed(2);
  // Prefer backend totals if provided; fallback to existing fields
  const totalBeforeDiscount = invoice.totalBeforeDiscount ?? undefined;
  const totalBeforeTax = invoice.totalBeforeTax ?? undefined;
  const totalTax = invoice.totalTax ?? undefined;
  const finalTotal = invoice.totalPriceFinal ?? invoice.totalPrice;
  const discountPercentage = invoice.discountPercentage ?? undefined;

  return (
    <div
      dir='rtl'
      style={{
        width: "58mm",
        padding: "0",
        background: "#fff",
        color: "#000",
        margin: "0 auto",
        border: "none",
        borderRadius: "8px",
        fontFamily: "Tahoma, Arial, sans-serif",
        fontSize: "14px",
        boxShadow: "none",
      }}
    >
      <style>{`
        *, *:focus { outline: none !important; -webkit-tap-highlight-color: transparent !important; }
        @media print {
          * {
            font-size: 14px !important;
            color: black !important;
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          .flex-between {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            flex-wrap: nowrap !important;
            page-break-inside: avoid !important;
          }
          img {
            max-height: 60px !important;
            image-rendering: crisp-edges;
          }
          table, th, td {
            border: 1px dashed #000 !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
        }
        .invoice-section { border-top: 1px dashed #000; padding-top: 8px; margin-top: 8px; }
        .flex-between { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; flex-wrap: nowrap; }
        .font-bold { font-weight: bold; }
        .font-normal { font-weight: normal; }
        .mt-4 { margin-top: 16px; }
        .text-center { text-align: center; }
        .space-y-1 > * + * { margin-top: 4px; }
        .footer { margin-top: 16px; text-align: center; }
        .items-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 14px !important; margin-top: 6px; }
        .items-table th { border: 1px dashed #000; padding: 3px; font-size: 10px !important; line-height: 1.2; text-align: center; background: #fff; font-weight: 600; }
        .items-table td { border: 1px dashed #000; padding: 3px; font-size: 14px !important; line-height: 1.4; text-align: center; }
        .items-table th:nth-child(1), .items-table td:nth-child(1) { width: 25%; }
        .items-table th:nth-child(2), .items-table td:nth-child(2) { width: 17%; }
        .items-table th:nth-child(3), .items-table td:nth-child(3) { width: 19%; }
        .items-table th:nth-child(4), .items-table td:nth-child(4) { width: 19%; }
        .items-table th:nth-child(5), .items-table td:nth-child(5) { width: 20%; }
        .totals-section { margin-top: 16px; font-weight: bold; font-size: 10px; }
        .totals-section .font-normal { font-weight: normal; }
        .header { text-align: center; margin-bottom: 8px; }
        .header .title { font-weight: bold; font-size: 20px; }
        .muted { color: #111; }
        .qr { display: flex; justify-content: center; margin-top: 10px; }
        .wrap { white-space: normal; word-break: break-word; overflow: visible; line-height: 1.4; }
      `}</style>

      {/* Saudi Header */}
      <div className='header'>
        {/* Logo (inline SVG for WebView compatibility) */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 12,
            marginTop: 12,
          }}
        >
          <LogoArSvg style={{ maxHeight: 48, height: 48 }} />
        </div>
        <hr
          style={{
            width: "90%",
            borderTop: "1px dashed #000",
            margin: "8px auto 10px",
          }}
        />
        <div className='title'>فاتورة ضريبية مبسطة</div>
        <div className='muted' style={{ marginTop: 4 }}>
          رقم الفاتورة: #{invoice.id}
        </div>
        <div className='font-bold' style={{ marginTop: 4 }}>
          شركة بندر بن عبدالحي كردي المحدودة
        </div>
        <div className='muted' style={{ marginTop: 2 }}>
          المدينة المنورة ،المدينة المنورة ،سعيد بن مقرن 42382,
        </div>
        <div className='flex-between' style={{ marginTop: 6 }}>
          <span>
            التاريخ: {invoice.date} - {invoice.time}
          </span>
          <span>رقم التسجيل الضريبي: 312174870400003</span>
        </div>
      </div>

      {/* Customer Info removed as requested */}

      {/* Items Table (dashed rows) */}
      <table className='items-table'>
        <thead>
          <tr>
            <th>منتج</th>
            <th>كمية</th>
            <th>قبل الضريبة</th>
            <th>الضريبة</th>
            <th>شامل</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const unitPre = Number(
              (item as any).price_before_tax ??
                (item as any).priceBeforeTax ??
                0
            );
            const unitTax = Number((item as any).tax ?? 0);
            const unitGross = Number((item as any).price ?? 0);
            return (
              <tr key={item.id}>
                <td className='wrap'>
                  {String(item.name)
                    .split(/\s+/)
                    .map((word, idx) => (
                      <span key={idx} style={{ display: "block" }}>
                        {word}
                      </span>
                    ))}
                </td>
                <td>{item.quantity}</td>
                <td>{fmt(unitPre)}</td>
                <td>{fmt(unitTax)}</td>
                <td>{fmt(unitGross)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Subscription Details (if any subscription purchased) */}
      {/* يتطلب تمرير الرد الكامل ليحتوي على order.items. في هذا القالب نعتمد على الحقول الموسعة في items */}
      {Array.isArray((invoice as any)?.order?.items) &&
      (invoice as any).order.items.some((it: any) => it.subscription_info) ? (
        <div className='invoice-section'>
          <div className='text-center font-bold' style={{ marginBottom: 6 }}>
            تفاصيل الاشتراك
          </div>
          {((invoice as any).order.items as any[])
            .filter((it: any) => Boolean(it.subscription_info))
            .map((it: any, idx: number) => {
              const sub = it.subscription_info;
              return (
                <div
                  key={idx}
                  className='space-y-1'
                  style={{ textAlign: "center" }}
                >
                  <div>الاسم: {sub?.name ?? "-"}</div>
                  <div>
                    عدد مرات الاستخدام المتاحة: {sub?.num_of_uses ?? "-"}
                  </div>
                  <div>المدة (أيام): {sub?.duration ?? "-"}</div>
                </div>
              );
            })}
        </div>
      ) : null}

      {/* Totals Section */}
      <div className='totals-section'>
        {typeof totalBeforeDiscount === "number" && (
          <div className='flex-between'>
            <span>قبل الخصم:</span>
            <span>{fmt(totalBeforeDiscount)} ريال</span>
          </div>
        )}
        {typeof discountPercentage === "number" && (
          <div className='flex-between font-normal'>
            <span>نسبة الخصم:</span>
            <span>
              {discountPercentage > 0
                ? `${discountPercentage.toFixed(2)}%`
                : "لا يوجد"}
            </span>
          </div>
        )}
        {typeof totalBeforeTax === "number" && (
          <div className='flex-between'>
            <span>قبل الضريبة:</span>
            <span>{fmt(totalBeforeTax)} ريال</span>
          </div>
        )}
        {typeof totalTax === "number" && (
          <div className='flex-between font-normal'>
            <span>الضريبة:</span>
            <span>{fmt(totalTax)} ريال</span>
          </div>
        )}
        <div className='flex-between'>
          <span>المبلغ المدفوع:</span>
          <span>{fmt(finalTotal)} ريال</span>
        </div>
        <div className='flex-between font-normal mt-4'>
          <span>مدفوع نقداً:</span>
          <span>{fmt(invoice.cashAmount)} ريال</span>
        </div>
        <div className='flex-between font-normal'>
          <span>مدفوع شبكة:</span>
          <span>{fmt(invoice.cardAmount)} ريال</span>
        </div>
      </div>

      {/* Footer */}
      <div className='footer'>
        <p>{"<شكرا لاستخدامكم خدمتنا>"}</p>
      </div>

      {/* QR Code (if provided) */}
      {invoice.qrCode ? (
        <div
          className='qr'
          style={{
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <QRCodeSVG value={String(invoice.qrCode)} size={120} includeMargin />
          <hr
            style={{ width: "90%", borderTop: "1px dashed #000", marginTop: 8 }}
          />
          <div style={{ textAlign: "center", marginTop: 6 }}>
            تم التطوير من خلال شركة اوريجامي
          </div>
          <div style={{ textAlign: "center", marginTop: 2 }}>origami.sa</div>
        </div>
      ) : null}
    </div>
  );
};

export default InvoicePrintContent;
